// frontend/src/pages/auth/SignUpPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import { Box, Button, TextField, Typography, Container, Divider, Alert } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

const SignUpPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignUp = async () => {
    try {
      setError('');
      setLoading(true);
      const { error } = await signInWithGoogle();
      if (error) throw error;
      navigate('/auth/callback');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      const { error } = await signUp(email, password, { full_name: name.trim() || email.split('@')[0] });
      if (error) throw error;
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResendMessage('');
      setResendLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      setResendMessage('Verification email resent. Please check your inbox.');
    } catch (err) {
      setResendMessage(`Could not resend email: ${err.message}`);
    } finally {
      setResendLoading(false);
    }
  };

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography component="h1" variant="h5">
            Check your email
          </Typography>
          <Alert severity="success" sx={{ width: '100%' }}>
            We sent a verification link to <strong>{email}</strong>. Click the link
            to activate your account.
          </Alert>
          {resendMessage && (
            <Alert
              severity={resendMessage.startsWith('Could not') ? 'error' : 'info'}
              sx={{ width: '100%' }}
            >
              {resendMessage}
            </Alert>
          )}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleResend}
            disabled={resendLoading}
          >
            {resendLoading ? 'Resending...' : "Didn't receive it? Resend email"}
          </Button>
          <Button fullWidth variant="text" onClick={() => navigate('/login')}>
            Already verified? Sign in
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <svg width="52" height="52" viewBox="8 14 138 138" xmlns="http://www.w3.org/2000/svg" aria-label="Release Cadence">
          <defs>
            <clipPath id="signup-body-clip">
              <path d="M48 18 L68 18 L88 100 L28 100 Z"/>
            </clipPath>
          </defs>
          <path d="M28 100 L88 100 L98 140 L18 140 Z" fill="#A87048"/>
          <path d="M48 18 L68 18 L88 100 L28 100 Z" fill="#241508"/>
          <rect x="54.5" y="22" width="7" height="78" fill="#EDE0C4" clipPath="url(#signup-body-clip)"/>
          <path d="M48 18 L68 18 L98 140 L18 140 Z" fill="none" stroke="#7B5030" strokeWidth="1.7" strokeLinejoin="round"/>
          <rect x="21" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
          <rect x="50.5" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
          <rect x="80" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
          <line x1="58" y1="100" x2="135.8" y2="45.5" stroke="#9B7050" strokeWidth="2.6" strokeLinecap="round"/>
          <circle cx="58" cy="100" r="3.5" fill="#9B7050"/>
          <path d="M100.2 60.1 L110.0 74.1 L95.1 80.8 L88.8 71.8 Z" fill="#C07838" stroke="#7B5030" strokeWidth="1.4" strokeLinejoin="round"/>
        </svg>
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, fontWeight: 600, letterSpacing: 0.5 }}>
          <span style={{ color: '#241508' }}>Release</span>
          <span style={{ color: '#7B5030' }}> Cadence</span>
        </Typography>
        <Typography component="h1" variant="h5">
          Sign Up
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <TextField
            margin="normal"
            fullWidth
            id="name"
            label="Full Name"
            name="name"
            autoComplete="name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
            onClick={() => navigate('/login')}
          >
            Already have an account? Sign In
          </Button>
          <Divider sx={{ my: 2 }}>OR</Divider>
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={handleGoogleSignUp}
            disabled={loading}
            startIcon={<GoogleIcon />}
          >
            Continue with Google
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default SignUpPage;